"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./Bacheca.css";


const REFRESH_MS = 5 * 60 * 1000;
const INITIAL_DATA = {
  condominio: { id: 1, denominazione: "VIA GIUSEPPE GARIBALDI 1", indirizzo: "Via Giuseppe Garibaldi, 1", cap: "80040", comune: "Cercola", citta: "NA" },
  amministratore: { denominazione: "Dott. Claudio Massimo Lapegna", indirizzo: "Via Lago Fusaro, 31 - Napoli", telefono: "+360810106680", cellulare: "+393661223287", email: "info@amministrazionilapegna.it", orariRicevimento: "Mar., Gio. 10:00-13:00\nMer., Ven. 16:00-19:00" },
  avvisi: [
    { id: 3, tipologia: "ALERT", titolo: "INTERVENTO DI DERATTIZZAZIONE", testoSecondario: "Si chiede di mantenere le finestre chiuse dalle 20:00 alle 23:00", priorita: 2 },
    { id: 4, tipologia: "AVVISO", titolo: "INTERVENTO STRAORDINARIO DI ESPURGO", testoSecondario: "In giornata sarà effettuato l'intervento straordinario di espurgo presso la scala 1", priorita: 2 },
  ],
  pubblicita: [{ id: 4, titolo: "Guaine sempre perfette", testoPrincipale: "Analisi della guaina e preventivo gratuito per la manutenzione", testoSecondario: "Possibilità di pagamenti rateizzati", telefono: "081 010 66 80", email: "", sitoWeb: "", durataVisualizzazione: 120, priorita: 2 }],
  aggiornatoAl: "2026-08-17T16:41:05.407",
};

function formatDate(date) {
  return new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(date);
}

function formatUpdated(value) {
  if (!value) return "Dati aggiornati";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Dati aggiornati";
  return `Aggiornato alle ${parsed.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
}

function initials(name) {
  const words = (name ?? "").replace(/Dott\.?/gi, "").trim().split(/\s+/).filter(Boolean);
  return `${words[0]?.[0] ?? "A"}${words.at(-1)?.[0] ?? ""}`.toUpperCase();
}

function noticeTone(index) {
  return ["blue", "gold", "green"][index % 3];
}

export default function Bacheca() {
  const { id = "1" } = useParams();
  const [now, setNow] = useState(null);
  const [data, setData] = useState(INITIAL_DATA);
  const [error, setError] = useState("");
  const [adIndex, setAdIndex] = useState(0);

  const loadData = useCallback(async (signal) => {
    try {
      const response = await fetch(`https://api.lapegna.xyz/bacheca/${id}`, { signal, cache: "no-store" });
      if (!response.ok) throw new Error(`Risposta API ${response.status}`);
      const payload = (await response.json());
      if (!payload.condominio || !payload.amministratore) throw new Error("Dati incompleti");
      setData(payload);
      setError("");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError("Impossibile aggiornare la bacheca. Nuovo tentativo automatico in corso.");
    }
  }, [id]);

  useEffect(() => {
    document.body.classList.add("bacheca-mode");
    return () => document.body.classList.remove("bacheca-mode");
  }, []);

  useEffect(() => {
    setNow(new Date());
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    const refresh = window.setInterval(() => loadData(), REFRESH_MS);
    return () => { controller.abort(); window.clearInterval(refresh); };
  }, [loadData]);

  const ads = data?.pubblicita ?? [];
  const currentAd = ads.length ? ads[adIndex % ads.length] : null;

  useEffect(() => {
    if (ads.length < 2 || !currentAd) return;
    const seconds = Math.max(3, Math.min(currentAd.durataVisualizzazione || 10, 120));
    const timer = window.setTimeout(() => setAdIndex((index) => (index + 1) % ads.length), seconds * 1000);
    return () => window.clearTimeout(timer);
  }, [ads.length, currentAd]);

  const { alert, standardNotices } = useMemo(() => {
    const notices = data?.avvisi ?? [];
    const urgent = notices.find((notice) => notice.tipologia.toUpperCase() === "ALERT" || notice.priorita === 1) ?? null;
    return { alert: urgent, standardNotices: notices.filter((notice) => notice.id !== urgent?.id).slice(0, 3) };
  }, [data?.avvisi]);

  if (!data) {
    return <main className="loading-screen"><div className="loading-building" aria-hidden="true"><i></i><i></i><i></i><i></i></div><h1>Bacheca condominiale</h1><p>{error || "Caricamento delle comunicazioni in corso…"}</p>{error && <button onClick={() => loadData()}>Riprova ora</button>}</main>;
  }

  const { condominio, amministratore } = data;
  const location = [condominio.indirizzo, condominio.cap, condominio.comune, condominio.citta ? `(${condominio.citta})` : ""].filter(Boolean).join(" · ");

  return (
    <main className="bacheca-page kiosk-shell">
      <header className="topbar">
        <div className="building-mark" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span><span></span></div>
        <div className="building-copy"><p>Bacheca digitale</p><h1>{condominio.denominazione}</h1><address>{location}</address></div>
        <div className="date-panel" aria-label="Data e ora attuali"><strong>{now ? now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "--:--"}</strong><span>{now ? formatDate(now) : "Caricamento data"}</span></div>
      </header>

      <div className="dashboard-grid">
        <aside className="side-panel admin-panel" aria-label="Contatti amministratore">
          <div><p className="eyebrow">Amministrazione</p><div className="avatar">{initials(amministratore.denominazione)}</div><h2>{amministratore.denominazione}</h2><p className="role">Amministrazione condominiale</p></div>
          <div className="contact-list">
            <div><span className="contact-icon">⌂</span><p><small>Ufficio</small>{amministratore.indirizzo}</p></div>
            <div><span className="contact-icon">☎</span><p><small>Telefono</small>{amministratore.telefono}</p></div>
            <div><span className="contact-icon">@</span><p><small>Email</small>{amministratore.email}</p></div>
          </div>
          <div className="office-hours"><span className="status-dot"></span><div><small>Ricevimento</small><strong>{amministratore.orariRicevimento}</strong></div></div>
          <div className="emergency-box"><small>Cellulare</small><strong>{amministratore.cellulare}</strong></div>
        </aside>

        <section className="noticeboard" aria-labelledby="notice-title">
          <div className="section-heading"><div><p className="eyebrow">In evidenza</p><h2 id="notice-title">Avvisi condominiali</h2></div><span className="updated"><i></i> {formatUpdated(data.aggiornatoAl)}</span></div>

          {alert && <article className="urgent-notice"><div className="urgent-icon">!</div><div><span>Avviso importante</span><h3>{alert.titolo}</h3><p>{alert.testoSecondario}</p></div><strong>ALERT</strong></article>}

          <div className={`notice-list notice-count-${standardNotices.length}`}>
            {standardNotices.map((notice, index) => <article className={`notice-card ${noticeTone(index)}`} key={notice.id}>
              <div className="notice-badge">{notice.tipologia.slice(0, 3).toUpperCase()}</div>
              <div className="notice-content"><span>{notice.tipologia}</span><h3>{notice.titolo}</h3><p>{notice.testoSecondario}</p></div><div className="arrow" aria-hidden="true">›</div>
            </article>)}
            {!standardNotices.length && <div className="empty-notices"><span>✓</span><h3>Nessun altro avviso</h3><p>Non sono presenti ulteriori comunicazioni attive.</p></div>}
          </div>

          <div className="ticker" aria-label="Stato collegamento"><span>LIVE</span><p>Le comunicazioni vengono aggiornate automaticamente ogni 5 minuti.</p></div>
        </section>

        <aside className="side-panel ad-panel" aria-label="Spazi pubblicitari">
          <div className="ad-heading"><p className="eyebrow">Servizi di zona</p><span>Pubblicità</span></div>
          {currentAd ? <article className="ad-card live-ad" key={currentAd.id}>
            {currentAd.immagineUrl ? <img src={currentAd.immagineUrl} alt="" /> : <div className="ad-logo">{initials(currentAd.titolo)}</div>}
            <p className="ad-kicker">Servizio consigliato</p><h3>{currentAd.titolo}</h3><p className="ad-main-copy">{currentAd.testoPrincipale}</p>
            {currentAd.testoSecondario && <p className="ad-secondary-copy">{currentAd.testoSecondario}</p>}
            {currentAd.telefono && <strong>{currentAd.telefono}</strong>}
            {currentAd.email && <small>{currentAd.email}</small>}
            {currentAd.sitoWeb && <small>{currentAd.sitoWeb}</small>}
          </article> : <div className="no-ad"><div className="ad-logo outline">SP</div><h3>Spazio disponibile</h3><p>Promuovi qui la tua attività.</p></div>}
          {ads.length > 1 && <div className="ad-dots" aria-label={`${ads.length} inserzioni`}>{ads.map((ad, index) => <i className={index === adIndex % ads.length ? "active" : ""} key={ad.id}></i>)}</div>}
          <p className="ad-cta">Vuoi apparire qui?<br /><strong>Contatta l&apos;amministrazione</strong></p>
        </aside>
      </div>

      <footer><span>Bacheca digitale offerta da <strong>CED ConTe S.r.l.</strong></span><span className={error ? "connection-warning" : ""}>{error ? "Aggiornamento temporaneamente non disponibile" : "Sistema aggiornato e operativo"}</span></footer>
    </main>
  );
}
